import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, connect } from 'react-redux';
import { LOGOUT } from '../redux/const/actionsTypes';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import Engdemy from '../assets/Images/Engdemy.png';
import './HomeLayout.css';

const BASE_URL =
    process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : '';

const navLinks = [
    { label: 'Home', href: `${BASE_URL}/` },
    { label: 'Courses', href: `${BASE_URL}/courses` },
    { label: 'Live Classes', href: `${BASE_URL}/classes` },
    { label: 'Pricing', href: `${BASE_URL}/pricing` },
];

const OldHomeLayout = ({ children, auth }) => {
    const [authenticated, setAuthenticated] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    // Auth check
    useEffect(() => {
        const savedAuthData = JSON.parse(localStorage.getItem('authData'));
        setAuthenticated(!!savedAuthData || !!auth.authData);
    }, [auth]);

    // Close mobile menu on route change
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    // Shadow on scroll
    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close mobile menu on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                !e.target.closest('#mobile-menu-panel') &&
                !e.target.closest('#mobile-menu-toggle')
            ) {
                setMobileOpen(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const handleLogOut = (e) => {
        e.preventDefault();
        dispatch({ type: LOGOUT });
        localStorage.removeItem('authData');
        toast.success('Logged out successfully');
        setAuthenticated(false);
        window.location.href = BASE_URL || '/';
    };

    const navigateToProfile = () => {
        navigate('/profile');
    };

    const isExamPage = location.pathname.startsWith('/fulltest') || location.pathname.startsWith('/listening') || location.pathname.startsWith('/reading') || location.pathname.startsWith('/writing') || location.pathname.startsWith('/speaking');
    if (isExamPage) return null;

    return (
        <>
            {/* ── Sticky Navbar ── */}
            <motion.nav
                initial={false}
                animate={{ y: 0 }}
                transition={{ type: 'spring', stiffness: 100, damping: 20 }}
                className={`nl-navbar${scrolled ? ' nl-scrolled' : ''}`}
                id="global-navbar"
            >
                <div className="nl-inner">
                    {/* Logo */}
                    <a
                        href={BASE_URL || '/'}
                        className="nl-logo"
                    >
                        <motion.div
                            whileHover={{ rotate: 12, scale: 1.1 }}
                            className="nl-logo-icon"
                        >
                            <img src={Engdemy} alt="Engdemy Logo" className="nl-logo-img" />
                        </motion.div>
                        <span className="nl-logo-text">Engdemy Education Inc</span>
                    </a>

                    {/* Desktop Nav Links */}
                    <nav className="nl-desktop-links">
                        {navLinks.map((link) => {
                            const isActive =
                                link.href === (BASE_URL || '') + '/'
                                    ? location.pathname === '/'
                                    : location.pathname.startsWith(
                                          link.href.replace(BASE_URL, '')
                                      );
                            return (
                                <a
                                    key={link.href}
                                    href={link.href}
                                    className={`nl-link${isActive ? ' nl-link--active' : ''}`}
                                >
                                    {link.label}
                                </a>
                            );
                        })}
                    </nav>

                    {/* Right: CTA / Auth + Hamburger */}
                    <div className="nl-right">
                        {/* Auth buttons (desktop) */}
                        {authenticated ? (
                            <div className="nl-auth-desktop">
                                <motion.span
                                    whileHover={{ scale: 1.05 }}
                                    onClick={navigateToProfile}
                                    className="nl-profile-btn"
                                    title="Go to Profile"
                                >
                                    <i className="fas fa-user" /> {auth.authData?.username}
                                </motion.span>
                                <motion.a
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    href="/"
                                    onClick={handleLogOut}
                                    className="nl-cta-btn"
                                >
                                    Logout
                                </motion.a>
                            </div>
                        ) : (
                            <div className="nl-auth-desktop">
                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <Link to="/login" className="nl-login-btn">
                                        Login
                                    </Link>
                                </motion.div>
                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <Link to="/signup" className="nl-cta-btn">
                                        Sign Up
                                    </Link>
                                </motion.div>
                            </div>
                        )}

                        {/* Mobile hamburger */}
                        <button
                            id="mobile-menu-toggle"
                            onClick={() => setMobileOpen((prev) => !prev)}
                            className="nl-hamburger"
                            aria-label="Toggle navigation menu"
                        >
                            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
                        </button>
                    </div>
                </div>
            </motion.nav>

            {/* ── Mobile Menu Overlay ── */}
            <AnimatePresence>
                {mobileOpen && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="nl-backdrop"
                            onClick={() => setMobileOpen(false)}
                        />

                        {/* Slide-down panel */}
                        <motion.div
                            id="mobile-menu-panel"
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                            className="nl-mobile-panel"
                        >
                            <div className="nl-mobile-links">
                                {navLinks.map((link) => {
                                    const isActive =
                                        link.href === (BASE_URL || '') + '/'
                                            ? location.pathname === '/'
                                            : location.pathname.startsWith(
                                                  link.href.replace(BASE_URL, '')
                                              );
                                    return (
                                        <a
                                            key={link.href}
                                            href={link.href}
                                            className={`nl-mobile-link${
                                                isActive ? ' nl-mobile-link--active' : ''
                                            }`}
                                            onClick={() => setMobileOpen(false)}
                                        >
                                            {link.label}
                                        </a>
                                    );
                                })}

                                {/* Mobile Auth */}
                                <div className="nl-mobile-auth">
                                    {authenticated ? (
                                        <>
                                            <span
                                                onClick={() => {
                                                    navigateToProfile();
                                                    setMobileOpen(false);
                                                }}
                                                className="nl-mobile-profile"
                                            >
                                                <i className="fas fa-user" /> {auth.authData?.username}
                                            </span>
                                            <a
                                                href="/"
                                                onClick={handleLogOut}
                                                className="nl-mobile-cta"
                                            >
                                                Logout
                                            </a>
                                        </>
                                    ) : (
                                        <>
                                            <Link
                                                to="/login"
                                                className="nl-mobile-login"
                                                onClick={() => setMobileOpen(false)}
                                            >
                                                Login
                                            </Link>
                                            <Link
                                                to="/signup"
                                                className="nl-mobile-cta"
                                                onClick={() => setMobileOpen(false)}
                                            >
                                                Sign Up
                                            </Link>
                                        </>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
};

const mapStateToProps = (state) => ({ auth: state.auth });

export default connect(mapStateToProps)(OldHomeLayout);
